import { accessTokenSignal, shareKeySignal } from "@src/auth/auth-utils";
import { z } from "zod";
import { WhiteboardDocumentId } from "./document-id";

export enum YAuthType {
	User = "user",
	WhiteboardCardCreate = "whiteboard-card-create",
}

const yAuthCredentialsPayloadSchema = z.object({
	type: z.union([
		z.nativeEnum(YAuthType),
		z.enum(["User", "WhiteboardCardCreate"]),
	]),
	token: z.string().optional(),
	whiteboardId: z.string().optional(),
	shareKey: z.string().nullable().optional(),
});

export type YAuthCredentialsPayload = z.infer<
	typeof yAuthCredentialsPayloadSchema
>;

export class YAuthCredentials {
	constructor(
		public type: YAuthType,
		public token?: string,
		public whiteboardId?: WhiteboardDocumentId,
		public shareKey: string | null = null,
	) {}

	static newForCard(
		token: string,
		whiteboardId: WhiteboardDocumentId,
		shareKey?: string,
	): YAuthCredentials {
		return new YAuthCredentials(
			YAuthType.WhiteboardCardCreate,
			token,
			whiteboardId,
			shareKey,
		);
	}

	static fromBase64(base64String: string): YAuthCredentials {
		const jsonString = atob(base64String);
		const parsed = yAuthCredentialsPayloadSchema.safeParse(
			JSON.parse(jsonString),
		);

		if (!parsed.success) {
			throw new Error("Invalid YAuthCredentials payload");
		}

		const obj = parsed.data;
		const type =
			obj.type === YAuthType.WhiteboardCardCreate ||
			obj.type === "WhiteboardCardCreate"
				? YAuthType.WhiteboardCardCreate
				: YAuthType.User;

		return new YAuthCredentials(
			type,
			obj.token,
			obj.whiteboardId ? new WhiteboardDocumentId(obj.whiteboardId) : undefined,
			obj.shareKey ?? null,
		);
	}

	toBase64(): string {
		const jsonString = JSON.stringify({
			type: this.type,
			token: this.token,
			whiteboardId: this.whiteboardId?.toIdString(),
			shareKey: this.shareKey,
		});
		return btoa(jsonString);
	}
}

/**
 * Resolves the latest collaboration token for `HocuspocusProvider`.
 *
 * When `fallbackAccessToken` is a Base64-encoded `YAuthCredentials` payload
 * (the production format created by `CollaborationProvider`), this rebuilds
 * the credentials using the freshest JWT from `refreshedJwt` or
 * `accessTokenSignal.value` and the latest `shareKeySignal.value`, so in-place
 * provider reconnections immediately authenticate with the refreshed token
 * without waiting for a React re-render or destroying the `HocuspocusProvider`.
 */
export function resolveCollaborationToken(
	fallbackAccessToken: string,
	whiteboardId?: WhiteboardDocumentId,
	refreshedJwt?: string | null,
): string {
	try {
		if (fallbackAccessToken) {
			const parsed = YAuthCredentials.fromBase64(fallbackAccessToken);
			const latestJwt =
				refreshedJwt ?? accessTokenSignal?.value ?? parsed.token ?? "";
			const latestShareKey = shareKeySignal?.value ?? parsed.shareKey ?? null;
			const effectiveWhiteboardId = whiteboardId ?? parsed.whiteboardId;
			const authType =
				effectiveWhiteboardId && latestJwt
					? YAuthType.WhiteboardCardCreate
					: YAuthType.User;

			return new YAuthCredentials(
				authType,
				latestJwt,
				effectiveWhiteboardId,
				latestShareKey,
			).toBase64();
		}
	} catch {
		// fallbackAccessToken is a raw token string rather than Base64 YAuthCredentials
	}

	return refreshedJwt ?? accessTokenSignal?.value ?? fallbackAccessToken;
}
